import { initWalletManager } from './events/initWalletManager.js';
import { activateEventBus } from './events/activateEventBus.js';
import { activateWalletEventListeners } from './listeners/index.js';
import { insertDealStarter } from './placements/dealStarter.js';
import { insertWalletBonnet } from './placements/walletBonnet.js';
import { insertWalletModal } from './placements/walletModal.js';
import { insertWalletGrid } from './placements/walletGrid.js';
import { insertSearchBudget } from './placements/walletSearchBudget.js';
import { insertTestDriveCTA } from './placements/vehicleCtas.js';
import { insertHeaderWallet } from './placements/walletHeader.js';
import { loadCSS } from './css/index.js';
import { loadWalletManager } from './events/loadWalletManager.js';
import { getTenant, getWalletScriptHost, isSectionsVDP } from './helpers/index.js';
import { ddcLog } from './utils/logger.js';

// Legacy imports for API-based integration
import { activateWalletEventListeners as activateWalletEventListenersLegacy } from './listeners/index.legacy.js';
import { insertDealStarter as insertDealStarterLegacy } from './placements/dealStarter.legacy.js';
import { insertWalletBonnet as insertWalletBonnetLegacy } from './placements/walletBonnet.legacy.js';
import { insertWalletModal as insertWalletModalLegacy } from './placements/walletModal.legacy.js';
import { insertWalletGrid as insertWalletGridLegacy } from './placements/walletGrid.legacy.js';
import { insertSearchBudget as insertSearchBudgetLegacy } from './placements/walletSearchBudget.legacy.js';
import { insertTestDriveCTA as insertTestDriveCTALegacy } from './placements/vehicleCtas.legacy.js';
import { insertHeaderWallet as insertHeaderWalletLegacy } from './placements/walletHeader.legacy.js';
import { loadCSS as loadCSSLegacy } from './css/index.legacy.js';
import { loadWalletManager as loadWalletManagerLegacy } from './events/loadWalletManager.legacy.js';

/**
 * Wait for DDC dataLayer to determine page type
 */
function waitForPageType() {
	return new Promise((resolve, reject) => {
		let checkCount = 0;
		const maxChecks = 200; // 10 seconds max with 50ms interval

		const checkInterval = setInterval(() => {
			checkCount += 1;

			// Check if DDC dataLayer exists
			const pageInfo = window.DDC?.dataLayer?.page?.pageInfo;
			if (!pageInfo) {
				if (checkCount >= maxChecks) {
					clearInterval(checkInterval);
					reject(new Error('DDC dataLayer not available'));
				}
				return;
			}

			// Check page type
			if (pageInfo.isVlp) {
				clearInterval(checkInterval);
				resolve({ pageType: 'SRP', vehicleUUID: null });
			} else if (pageInfo.isVdp) {
				// Get vehicle UUID from URL or first vehicle
				const vehicleUUID = window.DDC?.dataLayer?.vehicles?.[0]?.uuid || null;
				clearInterval(checkInterval);
				resolve({ pageType: 'VDP', vehicleUUID });
			} else {
				clearInterval(checkInterval);
				resolve({ pageType: 'OTHER', vehicleUUID: null });
			}
		}, 50); // Check every 50ms
	});
}

/**
 * Get site configuration from DDC PrivateAPI
 */
async function getConfig() {
	const integrationId = 'mywallet';
	const rawConfig = window.DDC?.PrivateAPI?.integrationConfigs?.[integrationId];

	// Extract config from v2 structure { fields: {...}, urls: {...} }
	return rawConfig?.fields && typeof rawConfig.fields === 'object'
		? rawConfig.fields
		: rawConfig || {};
}

/**
 * Get vehicle inventory data
 */
async function getVehicleData() {
	// Option 1: From global DDC.InvData
	if (window.DDC?.InvData && window.DDC.InvData.length > 0) {
		ddcLog('getVehicleData -> Using window.DDC.InvData, length:', window.DDC.InvData.length);
		return window.DDC.InvData;
	}

	// Option 2: On detail pages, try DDC.dataLayer.vehicles
	if (window.DDC?.dataLayer?.vehicles && window.DDC.dataLayer.vehicles.length > 0) {
		ddcLog('getVehicleData -> Using window.DDC.dataLayer.vehicles, length:', window.DDC.dataLayer.vehicles.length);
		return window.DDC.dataLayer.vehicles;
	}

	ddcLog('getVehicleData -> No vehicle data found, returning empty array');
	return [];
}

/**
 * Get dealer data
 */
async function getDealerData() {
	const dataLayer = window.DDC?.dataLayer || {};
	const dealership = dataLayer.dealership || {};
	const site = dataLayer.site || {};

	if (Object.keys(dealership).length > 0) {
		const dealerCodes = {};
		if (Array.isArray(dealership.dealerCode)) {
			dealership.dealerCode.forEach(entry => Object.assign(dealerCodes, entry));
		}

		return {
			dealershipAddress1: dealership.address1 || '',
			dealershipAddress2: dealership.address2 || '',
			dealershipCity: dealership.city || '',
			dealershipCodes: dealerCodes,
			dealershipCountry: dealership.country || '',
			dealershipFranchises: site.franchises || [],
			dealershipName: dealership.dealershipName || '',
			dealershipPostalCode: dealership.postalCode || '',
			dealershipStateProvince: dealership.stateProvince || ''
		};
	}

	// Fallback: use PrivateAPI dealer content if dataLayer has not populated yet.
	if (window.DDC?.PrivateAPI?.content?.dealer) {
		return window.DDC.PrivateAPI.content.dealer;
	}

	return {
		dealershipCodes: {}
	};
}

/**
 * Get site ID
 */
function getSiteId() {
	return (
		window.DDC?.dataLayer?.site?.siteId ||
		window.DDC?.siteId ||
		'unknown'
	);
}


/**
 * Main initialization function
 */
async function init() {
	try {
		ddcLog('Initializing MyWallet integration (standalone mode)');

		const { InvData } = window.DDC || {};

		// Wait for page type detection via DDC dataLayer
		const { pageType, vehicleUUID: _vehicleUUID } = await waitForPageType();

		// Map pageType to legacy format
		const searchPage = pageType === 'SRP';
		const detailPage = pageType === 'VDP';
		const indexPage = pageType === 'OTHER';

		// Detect layout type
		const layoutType = window.innerWidth < 768 ? 'mobile' : 'desktop';
		const isMobile = layoutType === 'mobile';

		// Get site ID
		const siteId = getSiteId();

		// Get design/themekit
		const siteDesign = window.DDC?.dataLayer?.site?.design || {};

		// Build page data object (similar to pageEvent.payload)
		const pageData = {
			searchPage,
			detailPage,
			indexPage,
			layoutType,
			siteId,
			design: siteDesign
		};

		ddcLog('pageData:', pageData);
		ddcLog('Page classification:', { searchPage, detailPage, indexPage, isMobile, siteId });

		// Get configuration
		const config = await getConfig();
		ddcLog('Config loaded:', config);

		const { useStaging, enableDealStarter, showHeaderPlacement, defaultDownPaymentPercent, enableEsntialUds = false } = config;
		const formatDownPaymentPercent = defaultDownPaymentPercent ? parseInt(defaultDownPaymentPercent, 10)/100 : 0.2;
		const walletSubdomain = useStaging ? 'staging' : 'www';
		const walletScriptHost = getWalletScriptHost(walletSubdomain);
		const walletScriptAssetsHost = `${walletScriptHost}/assets`;
		const walletManagerHost = `${walletScriptHost}/wallet-manager`;

		const isSRP = !!(searchPage && InvData);
		const isInventoryPage = !!(searchPage || detailPage);

		// Load CSS for theming and styling
		ddcLog('Loading CSS with config and pageData', { config, pageData });
		loadCSS(config, pageData);

		// Initialize event bus
		ddcLog('Activating event bus...');
		activateEventBus();

		// Get inventory for modal and listeners (only inventory pages will have inventory)
		const vehicles = isInventoryPage ? await getVehicleData() : [];
		ddcLog('Vehicles loaded:', vehicles);

		// Get dealer data for lease enablement check
		const dealerData = await getDealerData();

		// Activate wallet event listeners
		activateWalletEventListeners(isSRP, config, vehicles, indexPage, siteId, walletScriptAssetsHost, detailPage);

		// Insert the My Wallet Bonnet Shared Bundle
		ddcLog('Inserting wallet bonnet...');
		await insertWalletBonnet(walletScriptAssetsHost);

		// Load wallet manager script
		ddcLog('Loading wallet manager...');
		await loadWalletManager(walletManagerHost);

		// Initialize wallet manager
		ddcLog('Initializing wallet manager...');
		initWalletManager(useStaging, formatDownPaymentPercent, getTenant(config));

		if (detailPage && !enableEsntialUds && document.querySelector('#vehicle-build-offer')) {
			ddcLog('Loading UDS integration...');
			const { initializeUDSIntegration } = await import(
				/* webpackChunkName: 'uds' */
				'./integrations/uds.js'
			);
			await initializeUDSIntegration(config, vehicles);
		}

		// Insert the My Wallet Modal code
		ddcLog('Inserting wallet modal...');
		await insertWalletModal(walletScriptAssetsHost, config, vehicles, dealerData);

		// Insert header Wallet
		if(showHeaderPlacement){
			ddcLog('Inserting header wallet...');
			const { mywalletIcon } = config;
			insertHeaderWallet(isMobile, mywalletIcon);
		}

		if (!(isSRP || detailPage || indexPage)) {
			ddcLog('Not SRP, detail or index page. Aborting.');
			return;
		}

		// Insert the My Wallet Banner / Widget
		if (isSRP) {
			ddcLog('Inserting wallet grid for SRP...');
			await insertWalletGrid(walletScriptAssetsHost, config);
		} else if (detailPage) {
			if (!isSectionsVDP()) {
				ddcLog('Inserting test drive CTA...');
				insertTestDriveCTA(config);
			}

			// Insert Deal Starter
			if (enableDealStarter && vehicles.length > 0) {
				await insertDealStarter(walletScriptAssetsHost, config, vehicles[0]);
			}
		} else if (indexPage) {
			ddcLog('Inserting index page placements...');
			const {
				searchByBudget,
				showSiteTextSearchPlacement,
				showFacetBrowsePlacement,
				facetBrowseLinkColor,
				siteTextSearchLinkColor
			} = config;
			if(showSiteTextSearchPlacement){
				insertSearchBudget({ searchByBudget, linkColorConfig: siteTextSearchLinkColor }, 'site-text-search');
			}
			if(showFacetBrowsePlacement){
				insertSearchBudget({ searchByBudget, linkColorConfig: facetBrowseLinkColor }, 'facet-browse');
			}
		}

		ddcLog('Integration completed successfully (standalone mode)');
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Standalone: Error during integration:', error);
	}
}

// Initialize when DOM is ready
// Note: If this script is loaded with defer or at end of body, DOM will already be ready
function shouldUseNewWalletIntegration() {
	return window.location.search.includes('useNewWalletIntegration=true');
}

if (shouldUseNewWalletIntegration()) {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
} else {
	ddcLog('Initializing MyWallet integration (legacy mode)');
	// Load the legacy integration directly at the entry point so page-load-v1 event fires correctly
	(async APILoader => {

		const API = await APILoader.create(document.currentScript, 'mywallet');

		const { InvData } = window.DDC;
		API.subscribe('page-load-v1', async (pageEvent) => {
			try {
				const { payload: pageData } = pageEvent;
				ddcLog('pageData:', pageData);

				const { searchPage, detailPage, indexPage, layoutType, siteId } = pageData;
				const isMobile = layoutType === 'mobile';
				ddcLog('Page classification:', { searchPage, detailPage, indexPage, isMobile, siteId });

				const config = await API.utils.getConfig();
				ddcLog('Config loaded:', config);

				const { useStaging, enableDealStarter, showHeaderPlacement, defaultDownPaymentPercent, enableEsntialUds = false } = config;
				const formatDownPaymentPercent = defaultDownPaymentPercent ? parseInt(defaultDownPaymentPercent, 10)/100 : 0.2;
				const walletSubdomain = useStaging ? 'staging' : 'www';
				const walletScriptHost = getWalletScriptHost(walletSubdomain);
				const walletScriptAssetsHost = `${walletScriptHost}/assets`;
				const walletManagerHost = `${walletScriptHost}/wallet-manager`;

				const isSRP = !!(searchPage && InvData);
				const isInventoryPage = !!(searchPage || detailPage);

				ddcLog('Loading CSS with config and pageData', { config, pageData });
				loadCSSLegacy(API, config, pageData);

				ddcLog('Activating event bus...');
				activateEventBus();

				const vehicles = isInventoryPage ? await API.utils.getVehicleData() : [];
				ddcLog('Vehicles loaded:', vehicles);

				activateWalletEventListenersLegacy(API, isSRP, config, vehicles, indexPage, siteId, walletScriptAssetsHost, detailPage);

				ddcLog('Inserting wallet bonnet...');
				await insertWalletBonnetLegacy(API, walletScriptAssetsHost);

				ddcLog('Loading wallet manager...');
				await loadWalletManagerLegacy(API, walletManagerHost);

				ddcLog('Initializing wallet manager...');
				initWalletManager(useStaging, formatDownPaymentPercent, getTenant(config));

				if (detailPage && !enableEsntialUds && document.querySelector('#vehicle-build-offer')) {
					ddcLog('Loading UDS integration...');
					await import('./integrations/uds.legacy.js');
				}

				ddcLog('Inserting wallet modal...');
				insertWalletModalLegacy(API, walletScriptAssetsHost, config, vehicles);

				if(showHeaderPlacement){
					ddcLog('Inserting header wallet...');
					const { mywalletIcon } = config;
					insertHeaderWalletLegacy(API, isMobile, mywalletIcon);
				}

				if (!(isSRP || detailPage || indexPage)) {
					API.log('Not SRP, detail or index page. Aborting.');
					return;
				}

				if (isSRP) {
					ddcLog('Inserting wallet grid for SRP...');
					insertWalletGridLegacy(API, walletScriptAssetsHost, config);
				} else if (detailPage) {
					if (!isSectionsVDP()) {
						ddcLog('Inserting test drive CTA...');
						insertTestDriveCTALegacy(API, config);
					}
					if (enableDealStarter) {
						insertDealStarterLegacy(API, walletScriptAssetsHost, config);
					}
				} else if (indexPage) {
					ddcLog('Inserting index page placements...');
					const {
						searchByBudget,
						showSiteTextSearchPlacement,
						showFacetBrowsePlacement,
						facetBrowseLinkColor,
						siteTextSearchLinkColor
					} = config;
					if(showSiteTextSearchPlacement){
						insertSearchBudgetLegacy(API, {searchByBudget, linkColorConfig: siteTextSearchLinkColor}, 'site-text-search');
					}
					if(showFacetBrowsePlacement){
						insertSearchBudgetLegacy(API, {searchByBudget, linkColorConfig: facetBrowseLinkColor}, 'facet-browse');
					}
				}

				ddcLog('Integration completed successfully (legacy mode)');
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Legacy: Error during integration:', error);
			}
		});
	})(window.DDC.APILoader);
}
